/* ─────────────────────────────────────────────────────────────────────────
   page-transition.ts

   Slide-direction logic for the horizontal page slide.

   Primary: bottom-nav tab order. Going to a tab to the RIGHT of the
   current tab is "slide-forward" (content moves right→left); going LEFT
   is "slide-back" (content moves left→right).

   Fallback: when one or both routes aren't nav tabs (e.g. /leaderboard,
   detail pages), an in-memory history stack is used so drill-ins push
   forward and pops slide back.
   ─────────────────────────────────────────────────────────────────────── */

const EXCLUDED_ROUTES = ["/", "/arena"];

/**
 * Routes that use the cloud-cover transition (overlay-based, handled in
 * __root.tsx) instead of the horizontal slide. Both directions — entering
 * and leaving — are covered by clouds.
 */
const CLOUD_ROUTES = ["/world"];

const matchesRoute = (pathname: string, matcher: string) => {
  if (pathname === matcher) return true;
  if (matcher !== "/" && pathname.startsWith(`${matcher}/`)) return true;
  return false;
};

export const isCloudRoute = (pathname: string) =>
  CLOUD_ROUTES.some((m) => matchesRoute(pathname, m));

/**
 * The five tabs in the bottom nav, left → right.
 * Order MUST match HomeBottomNav items in (game)/route.tsx.
 */
const NAV_ORDER = ["/terminal", "/leaderboard", "/home", "/shop", "/missions"];

const navIndex = (pathname: string) =>
  NAV_ORDER.findIndex((p) => matchesRoute(pathname, p));

/**
 * True when the pathname is one of the bottom-nav tabs.
 * Used by __root to decide between slide (both nav) and pixel (at least
 * one non-nav, e.g. /match/$seed, /leaderboard, /missions, etc.).
 */
export const isNavbarRoute = (pathname: string) => navIndex(pathname) >= 0;

const navHistory: string[] = [];

/**
 * Returns the view-transition type for a navigation:
 *   • "slide-forward" — content moves right→left (next tab to the right, or new drill-in)
 *   • "slide-back"    — content moves left→right (tab to the left, or pop)
 *   • null            — same path, excluded route, or tab-hidden document
 */
export const getSlideDirection = (
  prevPathname: string,
  nextPathname: string,
): "slide-forward" | "slide-back" | null => {
  if (
    typeof document !== "undefined" &&
    document.visibilityState === "hidden"
  ) {
    return null;
  }

  if (!nextPathname || prevPathname === nextPathname) return null;

  const prevExcluded = EXCLUDED_ROUTES.some((m) =>
    matchesRoute(prevPathname, m),
  );
  const nextExcluded = EXCLUDED_ROUTES.some((m) =>
    matchesRoute(nextPathname, m),
  );
  if (prevExcluded || nextExcluded) return null;

  // Cloud-covered routes (e.g. /world) opt out of the slide; the cloud
  // overlay in __root.tsx handles those navigations.
  if (isCloudRoute(prevPathname) || isCloudRoute(nextPathname)) return null;

  // Both are nav tabs → direction by tab order.
  const fromIdx = navIndex(prevPathname);
  const toIdx = navIndex(nextPathname);
  if (fromIdx >= 0 && toIdx >= 0) {
    return toIdx > fromIdx ? "slide-forward" : "slide-back";
  }

  // Otherwise → history stack.
  if (navHistory.length === 0 && prevPathname) {
    navHistory.push(prevPathname);
  }

  const existingIdx = navHistory.indexOf(nextPathname);
  if (existingIdx >= 0 && existingIdx < navHistory.length - 1) {
    navHistory.length = existingIdx + 1;
    return "slide-back";
  }

  navHistory.push(nextPathname);
  return "slide-forward";
};

/* ── Legacy helpers (kept so existing callers keep compiling) ─────────── */

export const shouldUsePixelTransition = (
  prevPathname: string,
  nextPathname: string,
) => {
  const prevExcluded = EXCLUDED_ROUTES.some((m) =>
    matchesRoute(prevPathname, m),
  );
  const nextExcluded = EXCLUDED_ROUTES.some((m) =>
    matchesRoute(nextPathname, m),
  );
  return !prevExcluded && !nextExcluded;
};

export const pixelTransitionDefaults = {
  tileSize: 48,
  pixelColor: "#000",
  // backgroundColor: "oklch(96.7% 0.003 264.542)",
  animationDuration: 0.3,
  backgroundColor: "transparent",
  fadeDuration: 0.1,
};

export const cloudTransitionDefaults = {
  duration: 1000,
  holdDelay: 50,
  leftCloudSrc: "https://cdn.navalabs.io/clouds-top-left_kp6isp.png",
  rightCloudSrc: "https://cdn.navalabs.io/clouds-bottom-right_jmhdof.png",
};
